'use client';

import { analytics } from 'lib/analytics/analytics';
// import { heroDiscountCodes } from 'components/shared/Cart/utils';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import {
  ClueShopifyMap,
  isClueAvailable,
} from 'content/products/addons/clue';
import { RespiratoryShopifyMap } from 'content/products/addons/respiratory';
import {
  DieselRingColors,
  DieselRingPriceData,
  DieselRingSizes,
  DIESEL_RING_STATIC_SHIPPING,
  productMap_diesel_ring,
} from 'content/products/dieselRing';
import { UhxShopifyMap } from 'content/products/uhx';
// import {
//   ringReferralCodes,
//   getCampaignPromoDiscountConfig,
// } from 'content/referral';
// import { getCampaignConfig } from 'content/campaigns';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { useForwardSearchParamsShopify } from 'hooks/navigation/useForwardSearchParamsShopify';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { useCartStore, useShallow } from 'components/shared/Cart';
import {
  addonsReducer,
  getCheckoutLineItems,
  // getShippingEstimate,
  initialAddonsState,
  initialDieselRingSelectionContext,
  ShippingLabel,
} from './utils/checkout';
import { highlightElement } from './utils/highlightElement';
import { addonsType, DieselRingContextType } from './utils/types';
import { Currency } from 'utils/Currency';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { useIsCnOPlus } from 'hooks/pricing/useIsCnOPlus';

export const DieselRingSelectionContext = createContext<DieselRingContextType>(
  initialDieselRingSelectionContext,
);

export const DieselRingSelectionContextProvider: React.FC<{
  children?: React.ReactNode;
  currency: Currency;
  defaultColor: DieselRingColors;
  defaultSize: DieselRingSizes | null;
  defaultUhx: boolean;
  directCheckout?: boolean;
  notServicable?: boolean;
  notServicableMessage?: string;
  outOfStock?: boolean;
  isOpenSizeDisabled?: boolean;
}> = ({
  children,
  currency,
  defaultColor,
  defaultSize,
  defaultUhx,
  directCheckout,
  notServicable,
  notServicableMessage,
  outOfStock,
  isOpenSizeDisabled,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    // workAffiliateDiscount,
    store,
    addToCart,
    affiliateDetails,
    // getCampaignForProduct,
  } = useCartStore(
    useShallow((s) => ({
      store: s.store,
      addToCart: s.addToCart,
      affiliateDetails: s.affiliateDetails,
    })),
  );

  const campaignActive = false;
  const { isCnOPlus } = useIsCnOPlus();

  const showSizeRef = useRef<HTMLDivElement>(null);
  // No showChargerRef - removed for Diesel Ring
  // No engravingRef - removed for Diesel Ring
  const coverageRef = useRef<HTMLDivElement>(null);
  // No powerplugRef - removed for Diesel Ring
  const cartSummaryRef = useRef<HTMLDivElement>(null);

  const sectionRefs = {
    showSizeRef,
    coverageRef,
    cartSummaryRef,
  };

  // const ringCampaign = getCampaignForProduct('ring');
  // const campaignConfig = getCampaignConfig(ringCampaign);

  // const campaignActive = campaignConfig?.active ?? false;
  const colorParam = searchParams?.get('color');
  const [color, setColor] = useState<DieselRingColors>(
    colorParam &&
      (Object.values(DieselRingColors) as string[]).includes(colorParam)
      ? (colorParam as DieselRingColors)
      : defaultColor,
  );
  const [size, setSize] = useState<DieselRingSizes | null>(
    isOpenSizeDisabled ? null : defaultSize ?? DieselRingSizes.OPEN,
  );
  // No chargerType - removed for Diesel Ring
  const [uhx, setUhx] = useState<boolean>(defaultUhx);

  // No showEngravingSection - removed for Diesel Ring

  const [addonsState, dispatchAddons] = useReducer(
    addonsReducer,
    initialAddonsState,
  );
  const [isLoading, setIsLoading] = useState(false);

  const {
    afibCart,
    cardioAdaptibilityCart,
    cnoProCart,
    uhxCart,
    clueCart,
    respiratoryCart,
  } = addonsState;

  // No setEngraving or setEngravingMessage - removed for Diesel Ring
  const setUhxCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_UHX_CART', payload: value });
  const setAfibCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_AFIB_CART', payload: value });
  const setCardioAdaptabilityCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_CARDIO_ADAPTIBILITY_CART', payload: value });
  const setCnoProCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_CNO_PRO_CART', payload: value });
  const setClueCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_CLUE_CART', payload: value });
  const setRespiratoryCart = (value: addonsType) =>
    dispatchAddons({ type: 'SET_RESPIRATORY_CART', payload: value });

  const forwardShopifySearchParams = useForwardSearchParamsShopify();
  const eSizingSize = Boolean(searchParams?.get('eSizingSize'));

  // Clue flow detection
  const isClueFlow =
    searchParams?.get('flow') === 'clue' && isClueAvailable(store as ShopifyStore);

  // Respiratory flow detection
  const isRespiratoryFlow = searchParams?.get('flow') === 'respiratory';

  // Auto-enable clue addon when flow=clue
  useEffect(() => {
    if (isClueFlow && clueCart === null) {
      setClueCart('Year1');
    }
  }, [isClueFlow, clueCart]);

  // Auto-enable respiratory addon when flow=respiratory
  useEffect(() => {
    if (isRespiratoryFlow && respiratoryCart === null) {
      setRespiratoryCart('Year1');
    }
  }, [isRespiratoryFlow, respiratoryCart]);

  const requiredFields = [
    { ref: showSizeRef, condition: !size },
    // No charger validation - removed for Diesel Ring
    // No engraving validation - removed for Diesel Ring
    { ref: coverageRef, condition: uhx === false && uhxCart === null },
  ];

  const goToNextStep = () => {
    analytics.track('Bottom bar continue - CLICK');
    for (const { ref, condition } of requiredFields) {
      if (condition) {
        highlightElement(ref);
        break;
      }
    }
  };

  // Static shipping estimate for Diesel Ring
  // const shippingEstimate = useMemo(
  //   () => getShippingEstimate(),
  //   [], // No dependencies - always static
  // );

  const shippingEstimate = (
    <ShippingLabel
      content={
        <strong style={{ fontWeight: '500' }}>
          {DIESEL_RING_STATIC_SHIPPING}
        </strong>
      }
      justContent={true}
    />
  );

  // const referral = router.query.referral
  //   ? ringReferralCodes[store as ShopifyStore]
  //   : undefined;
  const referral = undefined;

  // const workAffiliate = router.query.workAffiliate && router.query.discount;
  const currencySymbol = CurrencySymbol[currency.toLowerCase() as Currency];

  const ultraHumanPrice = useMemo(
    () => (uhxCart && !uhx && store ? UhxShopifyMap[store][uhxCart].price : 0),
    [uhxCart, uhx, store],
  );

  const ringPriceObject =
    store && color
      ? DieselRingPriceData[store as ShopifyStore][color as DieselRingColors]
      : { price: 0, striked_price: 0 };

  // No engravingPrice - removed for Diesel Ring
  const priceDrop = ringPriceObject.striked_price - ringPriceObject.price;
  const price = ringPriceObject.price; // No engraving price to add

  // No chargerPrice - removed for Diesel Ring

  const afibPrice = useMemo(
    () =>
      afibCart ? AfibShopifyMap[store as ShopifyStore][afibCart].price : 0,
    [afibCart, store],
  );

  const cardioAdaptibilityPrice = useMemo(
    () =>
      cardioAdaptibilityCart
        ? CardioAdaptibilityShopifyMap[store as ShopifyStore][
            cardioAdaptibilityCart
          ].price
        : 0,
    [cardioAdaptibilityCart, store],
  );

  const cnoProPrice = useMemo(() => {
    if (!cnoProCart || !store) return 0;
    // Prices are consistent across Pro and Plus, always use Pro map for pricing
    return CnoProShopifyMap[store as ShopifyStore][cnoProCart].price;
  }, [cnoProCart, store]);

  const cluePrice = useMemo(() => {
    if (!clueCart) return 0;
    // When clue flow is active, use discounted price; otherwise use regular price
    if (isClueFlow) {
      return ClueShopifyMap[store as ShopifyStore]?.Year1?.discountedPrice || 0;
    }
    return ClueShopifyMap[store as ShopifyStore]?.Year1?.price || 0;
  }, [clueCart, store, isClueFlow]);

  const respiratoryPrice = useMemo(() => {
    if (!respiratoryCart) return 0;
    // When respiratory flow is active, use discounted price (FREE); otherwise use regular price
    if (isRespiratoryFlow) {
      return (
        RespiratoryShopifyMap[store as ShopifyStore]?.Year1?.discountedPrice ||
        0
      );
    }
    return RespiratoryShopifyMap[store as ShopifyStore]?.Year1?.price || 0;
  }, [respiratoryCart, store, isRespiratoryFlow]);

  const affiliateDiscount = useMemo(() => {
    // Exclude affiliate discount when affiliateCode=birthday
    if (searchParams?.get('affiliateCode') === 'birthday') {
      return 0;
    }

    const effectiveStore = getEffectiveStoreForDiscount(store);
    if (!effectiveStore) return 0;

    // Use ringDieselActive flag (NOT ringActive)
    if (
      !affiliateDetails ||
      !affiliateDetails.ringDieselActive ||
      !affiliateDetails.discount_configs[effectiveStore]?.RING_DIESEL?.code ||
      searchParams?.get('referral')
    )
      return 0;

    const discountConfig =
      affiliateDetails.discount_configs[effectiveStore]?.RING_DIESEL;

    return discountConfig?.type === 'percentage'
      ? (discountConfig.value * price) / 100
      : discountConfig?.value ?? 0;
  }, [
    affiliateDetails,
    price,
    store,
    searchParams,
  ]);
  // --- Hero Discount ---
  // const heroCategory =
  //   router.query.heroCategory &&
  //   heroDiscountCodes[router.query.heroCategory as string]
  //     ? router.query.heroCategory
  //     : null;
  // const heroDiscount = heroCategory ? price * 0.15 : 0;
  // const heroDiscount = 0;

  // --- Birthday Discount ---
  // Ring uses only 'discount' parameter (Shopify sync)
  // const birthdayDiscountCode = router.query.discount as string;
  // const affiliateCode = router.query.affiliateCode as string;

  // // Extract percentage from last 2 digits if they're numbers, otherwise default to 15%
  // let birthdayDiscountPercentage = 0; // default
  // if (birthdayDiscountCode && affiliateCode === 'birthday') {
  //   const lastTwoChars = birthdayDiscountCode.slice(-2);
  //   const parsedPercentage = parseInt(lastTwoChars);
  //   if (
  //     !isNaN(parsedPercentage) &&
  //     parsedPercentage > 0 &&
  //     parsedPercentage <= 100
  //   ) {
  //     birthdayDiscountPercentage = parsedPercentage;
  //   } else {
  //     birthdayDiscountPercentage = 15; // fallback to 15%
  //   }
  // }
  // const birthdayDiscount =
  //   birthdayDiscountCode &&
  //   birthdayDiscountCode.length > 0 &&
  //   affiliateCode === 'birthday'
  //     ? price * (birthdayDiscountPercentage / 100)
  //     : 0;
  // const birthdayDiscount = 0;

  // --- Ring Promo Discount ---
  // const ringPromoDiscountAmount = useMemo(() => {
  //   const promoConfig = getCampaignPromoDiscountConfig({
  //     store,
  //     country,
  //     product: 'ring',
  //     campaignConfig,
  //   });

  //   if (!promoConfig?.active || !promoConfig?.discount) {
  //     return 0;
  //   }

  //   const discountValue = promoConfig.discount;
  //   const discountType = promoConfig.discountType;

  //   return discountType === 'percentage'
  //     ? price * (discountValue / 100)
  //     : discountValue;
  // }, [store, country, price, campaignConfig]);
  // const ringPromoDiscountAmount = 0;

  const effectiveDiscount =
    affiliateDiscount > 0
      ? { type: 'affiliate', amount: affiliateDiscount }
      : { type: '', amount: 0 };

  const effectivePrice = useMemo(
    () =>
      price +
      // No chargerPrice - removed for Diesel Ring
      cardioAdaptibilityPrice +
      afibPrice +
      cnoProPrice +
      cluePrice +
      respiratoryPrice +
      ultraHumanPrice -
      effectiveDiscount.amount,
    [
      price,
      cardioAdaptibilityPrice,
      afibPrice,
      cnoProPrice,
      cluePrice,
      respiratoryPrice,
      ultraHumanPrice,
      effectiveDiscount,
    ],
  );

  // useEffect(() => {
  //   if (router.query['affiliate'] || router.query['affiliateCode']) {
  //     setUhxCart(false);
  //   }
  // }, [router.query]);

  const checkout = async () => {
    if (!color || !size || !store || notServicable === true || outOfStock === true) return;
    if (directCheckout) {
      if (
        productMap_diesel_ring[color]?.[size]?.NOT_ENGRAVED.variantId[
          store as ShopifyStore
        ]
      ) {
        const product =
          productMap_diesel_ring[color]?.[size]?.NOT_ENGRAVED.variantId[
            store as ShopifyStore
          ];
        const route = forwardShopifySearchParams(
          product as string,
          'diesel_ring',
        );
        router.push(route);
      } else {
        console.log('something went wrong!');
      }
      return;
    }
    try {
      setIsLoading(true);
      const lineItemsArray = getCheckoutLineItems({
        color,
        size,
        store,
        // No chargerType - removed for Diesel Ring
        // No engraving - removed for Diesel Ring
        // No engravingMessage - removed for Diesel Ring
        eSizingSize,
        uhxCart: uhxCart,
        afibCart: afibCart,
        cardioAdaptibilityCart: cardioAdaptibilityCart,
        cnoProCart: cnoProCart,
        clueCart: clueCart,
        respiratoryCart: respiratoryCart,
        isCnOPlus,
      });
      await addToCart(lineItemsArray, true);
    } catch (error) {
      alert('something went wrong!');
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DieselRingSelectionContext.Provider
      value={{
        color,
        setColor,
        size,
        setSize,
        // No chargerType - removed for Diesel Ring
        uhx,
        setUhx,
        uhxCart: uhxCart,
        setUhxCart,
        afibCart: afibCart,
        setAfibCart,
        cardioAdaptibilityCart: cardioAdaptibilityCart,
        setCardioAdaptabilityCart,
        cnoProCart: cnoProCart,
        setCnoProCart,
        clueCart: clueCart,
        setClueCart,
        respiratoryCart: respiratoryCart,
        setRespiratoryCart,
        // No engraving - removed for Diesel Ring
        // No engravingMessage - removed for Diesel Ring
        checkout,
        isLoading,
        defaultCurrency: currency,
        currencySymbol,
        store,
        price,
        ultraHumanPrice,
        effectivePrice,
        // No chargerPrice - removed for Diesel Ring
        shippingEstimate,
        affiliateDetails,
        campaignActive,
        affiliateDiscount,
        referral,
        sectionRefs,
        goToNextStep,
        effectiveDiscount,
        // No showEngravingSection - removed for Diesel Ring
        priceDrop,
        notServicable,
        notServicableMessage,
        outOfStock,
        isOpenSizeDisabled,
      }}
    >
      {children}
    </DieselRingSelectionContext.Provider>
  );
};
